export type Result<T> = { error: null; data: T } | { error: Error; data: null };

/**
 * Asserts the given value is not null or undefined.
 *
 * @param value - The value to assert.
 */
export function assertError(value: unknown): asserts value is Error {
  if (value === null || value === undefined) {
    throw new Error("Unreachable. Value should not be null or undefined.");
  }

  if (!(value instanceof Error)) {
    throw new Error("Invalid error object " + JSON.stringify(value));
  }
}

/// Map known server error codes (returned as the body of an ApiError) to
/// human-readable messages. Falls back to the raw error message.
const ERROR_CODE_MESSAGES: Record<string, string> = {
  max_spend_reached:
    "Spending cap reached. Raise the cap in billing settings, or wait for the next cycle.",
  billing_past_due:
    "Billing past due. Settle the outstanding invoice via the customer portal to resume.",
  account_pending_deletion:
    "Account is marked for deletion. Cancel the deletion in settings to make changes.",
  org_pending_deletion:
    "Organization is marked for deletion. Cancel the deletion in org settings to make changes.",
  free_task_limit: "Free plan monthly task limit reached. Upgrade to Pro for more.",
  free_project_limit: "Free plan project limit reached (4). Upgrade to Pro for more.",
  over_limit_freeze:
    "Your Free account exceeds the 4-project limit. Delete some projects or upgrade to Pro.",
  org_requires_team_plan:
    "This action requires the organization to be on the Team plan.",
  title_too_long: "Title is too long (200 characters max).",
  body_too_long: "Description is too long (400 characters max).",
};

export function getErrorMessage(e: unknown): string {
  const raw = e instanceof Error ? e.message : String(e);
  // ApiError format: "HTTP [/path] 403: max_spend_reached"
  for (const code of Object.keys(ERROR_CODE_MESSAGES)) {
    if (raw.endsWith(`: ${code}`) || raw === code) {
      return ERROR_CODE_MESSAGES[code];
    }
  }
  return raw;
}

export interface ErrorResponse<T> {
  error: T;
  response: { status: number };
}

type AnyErrorResponseFn = (
  ...args: never[]
) => ErrorResponse<unknown> | Promise<ErrorResponse<unknown>>;

export type QueryFn<T extends AnyErrorResponseFn> = Awaited<ReturnType<T>>;

export type QueryFnError<F extends AnyErrorResponseFn> = NonNullable<
  Awaited<ReturnType<F>>["error"]
> & {
  _status: Awaited<ReturnType<F>>["response"]["status"];
};

export function err<T extends ErrorResponse<unknown>>(res: T) {
  return { ...(res.error as object), _status: res.response.status };
}
